import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';

// ── Types ──

export type SimulationType = 'monte_carlo' | 'what_if' | 'forecast';

export interface SimulationConfig {
  type: SimulationType;
  name: string;
  parameters: Record<string, unknown>;
  entity_ids?: string[];
  iterations?: number;
  time_horizon_days?: number;
}

export interface SimulationResult {
  id: string;
  type: SimulationType;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  config: SimulationConfig;
  results: {
    summary: Record<string, unknown>;
    data_points: Array<Record<string, number | string>>;
    confidence_intervals?: Array<{
      label: string;
      lower: number;
      upper: number;
      mean: number;
    }>;
    risk_distribution?: Array<{ bucket: string; count: number }>;
    timeline?: Array<{ date: string; value: number; lower?: number; upper?: number }>;
  } | null;
  error?: string;
  created_at: string;
  completed_at: string | null;
  duration_ms: number | null;
}

export interface ForecastParams {
  entity_ids: string[];
  metric: string;
  time_horizon_days: number;
  model?: string;
}

export interface ForecastResult {
  id: string;
  metric: string;
  predictions: Array<{ date: string; value: number; lower: number; upper: number }>;
  model_accuracy: number;
  created_at: string;
}

// ── API Functions ──

async function runSimulation(config: SimulationConfig): Promise<SimulationResult> {
  // Backend has separate endpoints per type: /simulation/run, /simulation/monte-carlo, /simulation/what-if
  const typeEndpoint: Record<string, string> = {
    monte_carlo: '/simulation/monte-carlo',
    what_if: '/simulation/what-if',
  };
  const endpoint = typeEndpoint[config.type] || '/simulation/run';
  const { data } = await apiClient.post(endpoint, config);
  return data;
}

async function fetchSimulationResults(id: string): Promise<SimulationResult> {
  const { data } = await apiClient.get(`/simulation/results/${id}`);
  return data;
}

async function fetchSimulationHistory(_params?: {
  page?: number;
  page_size?: number;
  type?: SimulationType;
}): Promise<{ items: SimulationResult[]; total: number }> {
  // Backend does not have a list endpoint — return empty for now
  return { items: [], total: 0 };
}

async function runForecast(params: ForecastParams): Promise<ForecastResult> {
  const { data } = await apiClient.post('/simulation/forecast', params);
  return data;
}

// ── React Query Hooks ──

export function useRunSimulation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runSimulation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulations'] });
    },
  });
}

export function useSimulationResults(id: string | null) {
  return useQuery({
    queryKey: ['simulation', id],
    queryFn: () => fetchSimulationResults(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'pending' || data.status === 'running')) {
        return 3000;
      }
      return false;
    },
  });
}

export function useSimulationHistory(params?: {
  page?: number;
  page_size?: number;
  type?: SimulationType;
}) {
  return useQuery({
    queryKey: ['simulations', params],
    queryFn: () => fetchSimulationHistory(params),
    staleTime: 30_000,
  });
}

export function useRunForecast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runForecast,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simulations'] });
    },
  });
}
